import React, {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  useState,
} from "react";
import { Checkbox } from "@/components/common/Checkbox";
import { Dropdown } from "@/components/common/Dropdown";
import { Check, ListFilter } from "lucide-react";
import debounce from "lodash/debounce";
import Loader, { TableSkeleton } from "@/components/common/Loader";
import clsx from "clsx";
import Tooltip from "../Tooltip";

export interface BaseColumn {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  visible: boolean;
  hideOnMobile?: boolean;
  render?: (item: any, index: number) => React.ReactNode;
  className?: string;
  editable?: boolean;
  minWidth?: string;
}

export interface DropdownItem {
  id: string;
  label: string;
}

export interface DropdownConfig {
  [key: string]: {
    items: DropdownItem[];
    showSearch?: boolean;
    showAdd?: boolean;
    addLabel?: string;
    onAdd?: () => void;
    loading?: boolean;
  };
}

export interface BaseTableProps<T> {
  data: T[];
  columns: BaseColumn[];
  loading?: boolean;
  selectable?: boolean;
  selectedItems?: string[];
  onToggleSelection?: (id: string) => void;
  onToggleAllSelection?: (checked: boolean) => void;
  getItemId?: (item: T) => string;
  showFilters?: boolean;
  dropdownConfig?: DropdownConfig;
  selectedFilters?: { [key: string]: string[] };
  onFilterChange?: (columnKey: string, ids: string[]) => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  sortable?: boolean;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (columnKey: string) => void;
  onRowClick?: (item: T) => void;
  getRowClassName?: (item: T, index: number) => string;
  className?: string;
  tableClassName?: string;
  containerClassName?: string;
  emptyMessage?: string;
  footer?: React.ReactNode;
  selectionFooter?: (selectedCount: number) => React.ReactNode;
  newRow?: T | null;
  isNewRow?: (item: T) => boolean;
  enableExcelNavigation?: boolean;
  onCellSelect?: (
    rowIndex: number,
    colIndex: number,
    cell: HTMLElement
  ) => void;
  onCellEdit?: (
    rowIndex: number,
    colIndex: number,
    value: any,
    item: T
  ) => void;
  onCellDoubleClick?: (rowIndex: number, colIndex: number, item: T) => void;
  onCellEnter?: (rowIndex: number, colIndex: number, item: T) => void;
  isEditing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  headerClassName?: string;
  headerCellClassName?: string;
  rowClassName?: string;
  cellClassName?: string;
  bodyClassName?: string;
}

export function BaseTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  selectable = false,
  selectedItems = [],
  onToggleSelection,
  onToggleAllSelection,
  getItemId = (item) => item._id || item.id || "new-row",
  showFilters = false,
  dropdownConfig = {},
  selectedFilters = {},
  onFilterChange,
  searchTerm = "",
  onSearchChange,
  sortable = false,
  sortBy,
  sortDirection,
  onSort,
  onRowClick,
  getRowClassName,
  className = "",
  tableClassName = "task-table",
  containerClassName = "",
  emptyMessage = "No data found",
  footer,
  selectionFooter,
  newRow,
  isNewRow,
  enableExcelNavigation = true,
  onCellSelect,
  onCellEdit,
  onCellDoubleClick,
  onCellEnter,
  isEditing: externalIsEditing,
  onEditingChange,
  headerClassName = "",
  headerCellClassName = "",
  rowClassName = "",
  cellClassName = "",
  bodyClassName = "",
}: BaseTableProps<T>) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [currentCell, setCurrentCell] = useState<{ row: number; col: number }>({
    row: 0,
    col: 0,
  });
  const [internalIsEditing, setInternalIsEditing] = useState(false);

  const isEditing =
    externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  );

  const debouncedFilterChange = useRef(
    debounce((columnKey: string, ids: string[]) => {
      onFilterChange?.(columnKey, ids);
    }, 500)
  ).current;

  useEffect(() => {
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [debouncedFilterChange]);

  const focusCell = useCallback(
    (row: number, col: number) => {
      if (
        row < 0 ||
        row >= data.length ||
        col < 0 ||
        col >= visibleColumns.length
      )
        return;

      setCurrentCell({ row, col });
      const cellElement = tableRef.current?.querySelector(
        `[data-row="${row}"][data-col="${col}"]`
      ) as HTMLElement;
      if (cellElement) {
        cellElement.focus();
        onCellSelect?.(row, col, cellElement);
      }
    },
    [data.length, visibleColumns.length, onCellSelect]
  );

  const navigateCell = useCallback(
    (direction: "up" | "down" | "left" | "right") => {
      if (isEditing) return;

      const maxRow = data.length - 1;
      const maxCol = visibleColumns.length - 1;

      let newRow = currentCell.row;
      let newCol = currentCell.col;

      switch (direction) {
        case "up":
          newRow = Math.max(0, newRow - 1);
          break;
        case "down":
          newRow = Math.min(maxRow, newRow + 1);
          break;
        case "left":
          newCol = Math.max(0, newCol - 1);
          break;
        case "right":
          newCol = Math.min(maxCol, newCol + 1);
          break;
      }

      focusCell(newRow, newCol);
    },
    [currentCell, data.length, visibleColumns.length, isEditing, focusCell]
  );

  const handleEnterKey = useCallback(() => {
    if (isEditing) return;

    const item = data[currentCell.row];
    const column = visibleColumns[currentCell.col];

    if (item && column.editable) {
      onCellEnter?.(currentCell.row, currentCell.col, item);
      if (onEditingChange) {
        onEditingChange(true);
      } else {
        setInternalIsEditing(true);
      }
    }
  }, [
    currentCell,
    data,
    visibleColumns,
    isEditing,
    onCellEnter,
    onEditingChange,
  ]);

  const stopEditing = useCallback(() => {
    if (onEditingChange) {
      onEditingChange(false);
    } else {
      setInternalIsEditing(false);
    }
  }, [onEditingChange]);
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enableExcelNavigation) return;

      const activeElement = document.activeElement;
      const isInInput =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        activeElement?.tagName === "SELECT";

      if (isEditing) {
        if (e.key === "Escape") {
          e.preventDefault();
          stopEditing();
        }
        return;
      }

      if (isInInput) return;

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          navigateCell("up");
          break;
        case "ArrowDown":
          e.preventDefault();
          navigateCell("down");
          break;
        case "ArrowLeft":
          e.preventDefault();
          navigateCell("left");
          break;
        case "ArrowRight":
          e.preventDefault();
          navigateCell("right");
          break;
        case "Tab":
          // Only prevent default and navigate if not editing
          if (!isInInput) {
            e.preventDefault();
            if (e.shiftKey) {
              navigateCell("left");
            } else {
              navigateCell("right");
            }
          }
          break;
        case "Enter":
          e.preventDefault();
          handleEnterKey();
          break;
        case " ":
          e.preventDefault();
          if (selectable && !isEditing) {
            const item = data[currentCell.row];
            const itemId = getItemId(item);
            onToggleSelection?.(itemId);
          }
          break;
      }
    },
    [
      enableExcelNavigation,
      navigateCell,
      isEditing,
      selectable,
      data,
      currentCell,
      getItemId,
      onToggleSelection,
      handleEnterKey,
      stopEditing,
    ]
  );

  useEffect(() => {
    if (enableExcelNavigation) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [enableExcelNavigation, handleKeyDown]);

  useEffect(() => {
    if (enableExcelNavigation && data.length > 0) {
      focusCell(0, 0);
    }
  }, [enableExcelNavigation, data.length, focusCell]);

  const handleFilterChange = useCallback(
    (columnKey: string, ids: string[]) => {
      const allItems = dropdownConfig[columnKey]?.items || [];

      if (ids.length === 0 || ids.length === allItems.length) {
        onFilterChange?.(columnKey, []);
      } else {
        debouncedFilterChange(columnKey, ids);
      }
    },
    [dropdownConfig, debouncedFilterChange, onFilterChange]
  );

  const handleSort = useCallback(
    (columnKey: string) => {
      if (!sortable || !onSort) return;
      onSort(columnKey);
    },
    [sortable, onSort]
  );

  const isAllSelected = useMemo(() => {
    if (!selectable || data.length === 0) return false;
    const selectableItems = data.filter(
      (item) => !(newRow && isNewRow?.(item))
    );
    return (
      selectedItems.length === selectableItems.length &&
      selectableItems.length > 0
    );
  }, [selectable, data, selectedItems, newRow, isNewRow]);

  const isIndeterminate = useMemo(() => {
    if (!selectable || data.length === 0) return false;
    const selectableItems = data.filter(
      (item) => !(newRow && isNewRow?.(item))
    );
    return (
      selectedItems.length > 0 && selectedItems.length < selectableItems.length
    );
  }, [selectable, data, selectedItems, newRow, isNewRow]);

  const renderCell = useCallback(
    (item: T, column: BaseColumn, rowIndex: number, colIndex: number) => {
      const isCurrentCell =
        currentCell.row === rowIndex && currentCell.col === colIndex;
      const itemId = getItemId(item);

      if (column.render) {
        return column.render(item, rowIndex);
      }

      const value = item[column.key];
      let cellContent: React.ReactNode;

      if (typeof value === "object" && value !== null) {
        cellContent = value.name || value.label || JSON.stringify(value);
      } else {
        cellContent = value || "-";
      }

      return (
        <div
          data-excel-cell="true"
          data-editable={column.editable ? "true" : "false"}
          data-row={rowIndex}
          data-col={colIndex}
          data-item-id={itemId}
          tabIndex={0}
          className={clsx(
            "w-full h-full min-h-[24px] flex items-center",
            "outline-none transition-colors select-none",
            isCurrentCell && "bg-blue-50 ring-1 ring-blue-500",
            column.editable && "cursor-cell"
          )}
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (column.editable) {
              setCurrentCell({ row: rowIndex, col: colIndex });
              onCellEnter?.(rowIndex, colIndex, item);
              if (onEditingChange) {
                onEditingChange(true);
              } else {
                setInternalIsEditing(true);
              }
            }
            onCellDoubleClick?.(rowIndex, colIndex, item);
          }}
          onFocus={() => {
            focusCell(rowIndex, colIndex);
          }}
        >
          <div className="w-full truncate flex justify-center items-center">
            {cellContent}
          </div>
        </div>
      );
    },
    [
      currentCell,
      getItemId,
      focusCell,
      onCellEnter,
      onCellDoubleClick,
      onEditingChange,
    ]
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent, item: T, rowIndex: number) => {
      const target = e.target as HTMLElement;
      if (
        target.closest("button") ||
        target.closest("input") ||
        target.closest("select") ||
        target.closest(".dropdown-trigger") ||
        target.closest("[data-excel-cell]")
      ) {
        return;
      }

      if (onRowClick && !(newRow && isNewRow?.(item))) {
        onRowClick(item);
      }
    },
    [onRowClick, newRow, isNewRow]
  );

  const getEnhancedRowClassName = useCallback(
    (item: T, index: number) => {
      const isSelected = selectedItems.includes(getItemId(item));
      const isCurrentRow = currentCell.row === index;
      const baseClassName = getRowClassName?.(item, index) || "";
      const isNew = newRow && isNewRow?.(item);

      return clsx(
        baseClassName,
        "hover:bg-gray-50",
        isSelected && !isNew && "bg-blue-50",
        isCurrentRow && "ring-1 ring-blue-200"
      );
    },
    [
      selectedItems,
      getItemId,
      currentCell.row,
      getRowClassName,
      newRow,
      isNewRow,
    ]
  );

  return (
    <div
      className={clsx(
        "bg-white h-full flex flex-col overflow-hidden",
        className
      )}
    >
      <div className={clsx("flex-1 overflow-auto min-h-0", containerClassName)}>
    <table
        ref={tableRef}
        className={clsx(
          "min-w-full border-collapse",
          "whitespace-nowrap",
          "[&_td]:p-0 [&_th]:p-0",
          "[&_td]:h-[24px] [&_th]:h-[24px]",
          "[&_td]:border [&_td]:border-gray-300",
          "[&_th]:border [&_th]:border-gray-400",
          "[&_th]:bg-gray-100 [&_th]:font-medium [&_th]:text-xs",
          tableClassName
        )}
        style={{ tableLayout: "fixed" }}
      >
        <thead
          className={clsx("sticky top-0 z-10 bg-[#F5F5F5]", headerClassName)}
          style={{ height: '36px' }}
        >
          <tr>
            {selectable && (
              <th
                className={clsx(
                  "sticky left-0 z-[99] bg-gray-100 w-[40px] border border-gray-300",
                  headerCellClassName
                )}
                style={{ height: '24px' }}
              >
                <div 
                  className="flex items-center justify-center h-full px-1" 
                  style={{ height: '24px' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => {
                      e.stopPropagation();
                      console.log('🎯 Task selection checkbox clicked:', e.target.checked);
                      onToggleAllSelection?.(e.target.checked);
                    }}
                  />
                </div>
              </th>
            )}
            {visibleColumns.map((col, colIndex) => (
              <th
                key={col.key}
                className={clsx(
                  "border border-gray-300 text-sm font-medium bg-gray-100",
                  "whitespace-nowrap overflow-hidden",
                  col.sortable && sortable && "cursor-pointer hover:bg-gray-200",
                  col.hideOnMobile && "hidden sm:table-cell",
                  col.className,
                  headerCellClassName
                )}
                style={{
                  minWidth: col.minWidth || (col.key === "actions" ? "120px" : "100px"),
                  width: col.width,
                  height: '24px',
                }}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                <Tooltip content={col.label} >
                <div
                  className={clsx(
                    "flex items-center h-full px-2 w-full cursor-pointer",
                    (colIndex === 0 && !selectable) || (colIndex === 0 && selectable)
                      ? "justify-start"
                      : headerClassName?.includes("text-right") ||
                        headerCellClassName?.includes("text-right")
                      ? "justify-end"
                      : headerClassName?.includes("text-center") ||
                        headerCellClassName?.includes("text-center")
                      ? "justify-center"
                      : "justify-between"
                  )}
                  style={{ height: '24px' }}
                >
                  <span className="truncate text-xs">{col.label}</span>
                  {showFilters && dropdownConfig[col.key] && (
                    <Dropdown
                      button={
                        <span
                          className="text-blue-600 dropdown-trigger flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ListFilter
                            size={12}
                            className="text-gray-500 hover:text-gray-700"
                          />
                        </span>
                      }
                      items={dropdownConfig[col.key].items}
                      selected={selectedFilters[col.key] || []}
                      onChange={(ids) => {
                        console.log(`🎯 Filter dropdown onChange called for ${col.key}:`, ids);
                        console.log('🎯 Filter call stack:', new Error().stack);
                        handleFilterChange?.(col.key, ids);
                      }}
                      withCheckboxes
                      withSelectAll
                        treatEmptyAsAll={true}  // Add this line!
                      onAddClick={dropdownConfig[col.key].onAdd}
                      addLabel={dropdownConfig[col.key].addLabel}
                      loading={dropdownConfig[col.key].loading}
                      showSearchBar={dropdownConfig[col.key].showSearch}
                      showAddButton={dropdownConfig[col.key].showAdd}
                      searchTerm={searchTerm}
                      setSearchTerm={onSearchChange}
                    />
                  )}
                </div>
                </Tooltip>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                className="text-center py-10"
              >
                <TableSkeleton />                
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                className="py-10"
              >
                <div className="flex justify-center items-center h-[60vh] w-full text-center">
                  <div className="flex flex-col justify-center items-center text-center space-y-2">
                    <Check size={24} />
                    <span>{emptyMessage}</span>
                  </div>
                </div>
              </td>
            </tr>
          ) : (
            data.map((item, rowIndex) => {
              const itemId = getItemId(item);
              const isSelected = selectedItems.includes(itemId);
              const isNew = newRow && isNewRow?.(item);

              return (
                <tr
                  key={itemId}
                  className={`${getEnhancedRowClassName(item, rowIndex)} ${rowClassName}`}
                  style={{ height: '24px' }}
                  onClick={(e) => handleRowClick(e, item, rowIndex)}
                >
                  {selectable && (
                    <td
                      className="border border-gray-300 p-0 bg-white sticky left-0 z-10"
                      style={{ width: "40px", height: '24px' }}
                    >
                      <div className="flex items-center justify-center h-full px-1" style={{ height: '24px' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            onToggleSelection?.(itemId);
                          }}
                          disabled={isNew}
                        />
                      </div>
                    </td>
                  )}
                  {visibleColumns.map((col, colIndex) => (
                    <td
                      key={col.key}
                      className={clsx(
                        "border border-gray-300 p-0",
                        "whitespace-nowrap overflow-hidden",
                        col.hideOnMobile && "hidden sm:table-cell",
                        col.className,
                        cellClassName
                      )}
                      style={{
                        minWidth: col.minWidth || "100px",
                        maxWidth: "300px",
                        height: "24px",
                      }}
                    >
                      {renderCell(item, col, rowIndex, colIndex)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      {selectionFooter && selectedItems.length > 0 && (
        <div className="bg-gray-50 px-4 sm:px-6 py-2 border-t border-gray-200">
          {selectionFooter(selectedItems.length)}
        </div>
      )}

      {footer && <div className="border-t border-gray-200">{footer}</div>}
    </div>
  );
}

export const BaseTableWithRef = forwardRef<
  HTMLTableElement,
  BaseTableProps<any>
>((props, ref) => {
  return <BaseTable {...props} />;
});

export default BaseTable;